import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useCart } from "../../contexts/CartContext";
import { useStoreAuth } from "../../contexts/StoreAuthContext";
import { CheckoutFormValues } from "../../types/store";

const CheckoutPage: React.FC = () => {
    const { items, subtotal, total, hasPhysicalItem, clearCart } = useCart();
    const { user } = useStoreAuth();
    const [status, setStatus] = useState<"idle" | "processing" | "success">("idle");
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors }
    } = useForm<CheckoutFormValues>({
        defaultValues: {
            email: user?.email ?? "",
            name: user?.name ?? "",
            createAccount: !user
        }
    });

    const createAccountChecked = watch("createAccount");
    const passwordValue = watch("password");

    const onSubmit = handleSubmit(async (values) => {
        setStatus("processing");
        await new Promise((resolve) => setTimeout(resolve, 900));
        console.log("checkout payload", {
            customer: user ?? values,
            items,
            summary: { subtotal, total }
        });
        setStatus("success");
        clearCart();
    });

    const requiresShipping = hasPhysicalItem;

    const summaryCopy = useMemo(
        () => ({
            heading: user
                ? "Your purchase will be saved in your Modelerman account."
                : "Guest checkout — we will email downloads and receipts.",
            hint: user
                ? "Update details anytime from My Account › Profile."
                : "You can optionally create a free account and keep everything in one place."
        }),
        [user]
    );

    if (items.length === 0 && status !== "success") {
        return (
            <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 p-12 text-center text-white/70">
                Your cart is empty. Explore the <a className="underline" href="/store">Modelerman Store</a> to add items.
            </div>
        );
    }

    return (
        <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr]">
            <section className="rounded-[28px] border border-white/10 bg-white/5 p-8">
                <div className="space-y-1">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/50">Checkout</p>
                    <h1 className="text-3xl font-semibold">Guest checkout + optional account</h1>
                    <p className="text-sm text-white/60">{summaryCopy.heading}</p>
                    <p className="text-xs text-white/50">{summaryCopy.hint}</p>
                </div>

                {status === "success" ? (
                    <div className="mt-8 rounded-2xl border border-emerald-400/40 bg-emerald-400/10 p-6">
                        <p className="text-lg font-semibold text-emerald-300">Payment success (mock)</p>
                        <p className="text-sm text-white/70">
                            We logged the payload in the console. You can extend this hook with a real payment gateway later.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="mt-8 space-y-6">
                        <div className="grid gap-6 md:grid-cols-2">
                            <label className="text-sm text-white/80">
                                Email address
                                <input
                                    type="email"
                                    className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                    required
                                    placeholder="you@modelerman.com"
                                    {...register("email", { required: "Email is required" })}
                                />
                                {errors.email && <span className="text-xs text-red-400">{errors.email.message}</span>}
                            </label>

                            <label className="text-sm text-white/80">
                                Name (optional)
                                <input
                                    type="text"
                                    className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                    placeholder="Nora Modeler"
                                    {...register("name")}
                                />
                            </label>
                        </div>

                        {requiresShipping && (
                            <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5">
                                <p className="text-sm font-semibold">Shipping address</p>
                                <label className="text-sm text-white/80">
                                    Country
                                    <input
                                        className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                        placeholder="Türkiye"
                                        {...register("shipping.country", { required: "Country is required" })}
                                    />
                                    {errors.shipping?.country && (
                                        <span className="text-xs text-red-400">{errors.shipping.country.message}</span>
                                    )}
                                </label>
                                <label className="text-sm text-white/80">
                                    City
                                    <input
                                        className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                        placeholder="İstanbul"
                                        {...register("shipping.city", { required: "City is required" })}
                                    />
                                    {errors.shipping?.city && (
                                        <span className="text-xs text-red-400">{errors.shipping.city.message}</span>
                                    )}
                                </label>
                                <label className="text-sm text-white/80">
                                    Full address
                                    <textarea
                                        className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                        rows={3}
                                        placeholder="Street, number, reference"
                                        {...register("shipping.addressLine", { required: "Address is required" })}
                                    />
                                    {errors.shipping?.addressLine && (
                                        <span className="text-xs text-red-400">{errors.shipping.addressLine.message}</span>
                                    )}
                                </label>
                                <label className="text-sm text-white/80">
                                    Phone
                                    <input
                                        className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                        placeholder="+90 555 123 45 67"
                                        {...register("shipping.phone", { required: "Phone is required" })}
                                    />
                                    {errors.shipping?.phone && (
                                        <span className="text-xs text-red-400">{errors.shipping.phone.message}</span>
                                    )}
                                </label>
                            </div>
                        )}

                        {!user && (
                            <label className="flex items-start gap-3 text-sm text-white/80">
                                <input type="checkbox" className="mt-1" {...register("createAccount")} />
                                <span>Create a free Modelerman account and keep all my purchases in one place.</span>
                            </label>
                        )}

                        {(!user && createAccountChecked) && (
                            <div className="grid gap-6 md:grid-cols-2">
                                <label className="text-sm text-white/80">
                                    Password
                                    <input
                                        type="password"
                                        className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                        placeholder="Minimum 6 characters"
                                        {...register("password", {
                                            required: "Password is required",
                                            minLength: { value: 6, message: "Use at least 6 characters" }
                                        })}
                                    />
                                    {errors.password && <span className="text-xs text-red-400">{errors.password.message}</span>}
                                </label>
                                <label className="text-sm text-white/80">
                                    Confirm password
                                    <input
                                        type="password"
                                        className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                                        placeholder="Repeat password"
                                        {...register("confirmPassword", {
                                            required: "Please confirm password",
                                            validate: (value) => value === passwordValue || "Passwords do not match"
                                        })}
                                    />
                                    {errors.confirmPassword && (
                                        <span className="text-xs text-red-400">{errors.confirmPassword.message}</span>
                                    )}
                                </label>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status === "processing"}
                            className="w-full rounded-2xl bg-accent py-4 text-base font-semibold text-black"
                        >
                            {status === "processing" ? "Processing..." : "Place order (mock)"}
                        </button>
                    </form>
                )}
            </section>

            <aside className="space-y-6 rounded-[28px] border border-white/10 bg-white/5 p-8">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/50">Order summary</p>
                    <div className="mt-4 space-y-4">
                        {items.map((item) => (
                            <div key={item.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-semibold">{item.name}</p>
                                        <p className="text-xs text-white/60">{item.productType} • Qty {item.quantity}</p>
                                        {item.license && <p className="text-xs text-white/50">License: {item.license}</p>}
                                    </div>
                                    <p className="text-sm font-semibold">${(item.unitPrice * item.quantity).toFixed(2)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between text-white/70">
                        <span>Subtotal</span>
                        <span>${subtotal.toFixed(2)}</span>
                    </div>
                    {hasPhysicalItem && (
                        <div className="flex items-center justify-between text-white/70">
                            <span>Shipping</span>
                            <span>Calculated at checkout</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between text-white">
                        <span className="font-semibold">Total</span>
                        <span className="text-xl font-semibold">${total.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-white/50">VAT text placeholder. Add your own logic once tax rules are ready.</p>
                </div>
            </aside>
        </div>
    );
};

export default CheckoutPage;
